import {
  ANOMALY_LABELS,
  confidenceLabel,
  formatMinor,
  type JobSuggestion,
  type RecordInsights,
  type Suggestion,
} from '@hvac/contracts';
import { Alert, Button, Card, CardBody, CardHeader } from '@hvac/ui';
import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { recordInsightsQuery } from '../../shared/api/intelligence-queries.js';

/**
 * What the engine noticed.
 *
 * Every suggestion on this panel shows the counts it came from, because a
 * bookkeeper who cannot check a suggestion will either take it on faith or
 * ignore it, and both are worse than no suggestion. "Equipment, 98%" is a
 * claim. "Coded to Equipment on 842 of 844 records from this supplier, most
 * recently yesterday" is evidence.
 */

export interface InsightsPanelProps {
  readonly organizationId: string;
  readonly recordId: string;
  readonly categoryLabelFor: (categoryId: string) => string | null;
  readonly onApplyCategory: (categoryId: string) => void;
  readonly onApplyJob: (jobId: string) => void;
  readonly editable: boolean;
}

export function InsightsPanel({
  organizationId,
  recordId,
  categoryLabelFor,
  onApplyCategory,
  onApplyJob,
  editable,
}: InsightsPanelProps) {
  const insights = useQuery(recordInsightsQuery(organizationId, recordId));

  if (insights.isPending) {
    return (
      <Card>
        <CardHeader title="What the engine noticed" />
        <CardBody>
          <p className="text-sm text-content-muted">Looking through this supplier’s history…</p>
        </CardBody>
      </Card>
    );
  }

  if (insights.isError) {
    return (
      <Card>
        <CardHeader title="What the engine noticed" />
        <CardBody>
          <p className="text-sm text-content-muted">
            The history could not be read just now. Nothing on this record depends on it.
          </p>
        </CardBody>
      </Card>
    );
  }

  const data: RecordInsights = insights.data;
  const hasAnything =
    data.category !== null ||
    data.anomalies.length > 0 ||
    data.duplicates.length > 0 ||
    data.jobs.length > 0;

  return (
    <Card>
      <CardHeader
        title="What the engine noticed"
        description="Counted from records a person confirmed. Nothing here is a model’s opinion."
      />
      <CardBody className="flex flex-col gap-4">
        {!hasAnything ? (
          <p className="text-sm text-content-muted">
            Nothing yet. This supplier has no confirmed history to learn from, which is the expected
            state until a few records go through.
          </p>
        ) : null}

        {data.anomalies.map((anomaly) => (
          <Alert
            key={anomaly.code + anomaly.message}
            tone="warning"
            title={ANOMALY_LABELS[anomaly.code]}
          >
            {anomaly.message}
          </Alert>
        ))}

        {data.duplicates.length === 0 ? null : (
          <Alert tone="danger" title="This may already be recorded">
            <ul className="mt-1 flex flex-col gap-1">
              {data.duplicates.map((candidate) => (
                <li key={candidate.transactionId}>
                  <Link
                    to="/bookkeeping/$recordId"
                    params={{ recordId: candidate.transactionId }}
                    className="underline"
                  >
                    {candidate.transactionDate} · {formatMinor(candidate.grossAmountMinor)}
                    {candidate.reference === null ? '' : ` · ${candidate.reference}`}
                  </Link>
                  <span className="block text-xs">{candidate.reason}</span>
                </li>
              ))}
            </ul>
          </Alert>
        )}

        {data.category === null ? null : (
          <SuggestionRow
            heading="Account"
            suggestion={data.category}
            label={categoryLabelFor(data.category.value) ?? 'A category'}
            onApply={editable ? () => onApplyCategory(data.category?.value ?? '') : undefined}
          />
        )}

        {data.classification === null ? null : (
          <SuggestionRow
            heading="Treatment"
            suggestion={data.classification}
            label={data.classification.value.replaceAll('_', ' ')}
          />
        )}

        {data.jobs.length === 0 ? null : (
          <div>
            <p className="text-sm font-medium text-content">Jobs this could belong to</p>
            <p className="mb-2 text-xs text-content-muted">
              A shortlist, not a prediction. A receipt does not know which job it is for.
            </p>
            <ul className="flex flex-col gap-2">
              {data.jobs.map((job) => (
                <JobRow
                  key={job.jobId}
                  job={job}
                  onApply={editable ? () => onApplyJob(job.jobId) : undefined}
                />
              ))}
            </ul>
          </div>
        )}
      </CardBody>
    </Card>
  );
}

function SuggestionRow({
  heading,
  suggestion,
  label,
  onApply,
}: {
  heading: string;
  suggestion: Suggestion;
  label: string;
  onApply?: (() => void) | undefined;
}) {
  const { evidence } = suggestion;

  return (
    <div className="rounded-[var(--radius-surface)] border border-border bg-surface-sunken p-3">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-xs uppercase tracking-wide text-content-subtle">{heading}</p>
          <p className="text-sm font-medium text-content">{label}</p>
        </div>
        {onApply === undefined ? null : (
          <Button size="sm" variant="secondary" onClick={onApply}>
            Use this
          </Button>
        )}
      </div>

      <p className="mt-2 text-sm text-content-muted">{evidence.basis}</p>

      {/* The number stays visible beside the words. 0.82 and 0.86 are the same
          decision, but somebody will always want to see the figure. */}
      <p className="mt-1 text-xs text-content-subtle">
        {confidenceLabel(suggestion.confidence)} · {(suggestion.confidence * 100).toFixed(1)}%
        {evidence.of > 0 ? ` · ${evidence.observed} of ${evidence.of} records` : ''}
        {evidence.correctedAway > 0
          ? ` · moved off ${evidence.correctedAway} time${evidence.correctedAway === 1 ? '' : 's'}`
          : ''}
      </p>
    </div>
  );
}

function JobRow({ job, onApply }: { job: JobSuggestion; onApply?: (() => void) | undefined }) {
  return (
    <li className="rounded-[var(--radius-surface)] border border-border bg-surface-sunken p-3">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-sm font-medium text-content">
            {job.jobNumber === null ? job.title : `${job.jobNumber}, ${job.title}`}
          </p>
          {job.customerName === null ? null : (
            <p className="text-xs text-content-muted">{job.customerName}</p>
          )}
        </div>
        {onApply === undefined ? null : (
          <Button size="sm" variant="secondary" onClick={onApply}>
            Link
          </Button>
        )}
      </div>
      <ul className="mt-1 list-inside list-disc text-xs text-content-muted">
        {job.reasons.map((reason) => (
          <li key={reason}>{reason}</li>
        ))}
      </ul>
    </li>
  );
}
